// Product Line Reclassification — the screen's strings in the portal's four languages (2026-10-21).
// Codes stay as SAP writes them (FERT, HAWA, UNBW, DIEN, line codes such as HXW, brand 41); the six NAME columns
// carry the words Miley uses (Current Brand, New Brand, Current Line, New Line, Category, Sub Category).
const en = {
  title: 'Product Line Reclassification',
  subtitle: 'New hierarchy code and six tags, held in the portal — SAP, NetSuite and PLM are read only',
  whereStands: 'Where the mapping stands',
  refresh: 'Refresh',
  mappingNote: 'The new code is the SAP code with its <b>Brand</b> and <b>Line</b> segments replaced — Category and Sub-category carry through unchanged. The brand follows the line a style is moved onto, and can also be set for one style on the Edit row. Nothing is written back to SAP, NetSuite or PLM, and the new classification is <b>not applied on any other screen yet</b>.',
  stat: {
    mapped: 'Mapped',
    needsDecision: 'Needs a decision',
    noRule: 'No rule yet',
    rules: 'Rules',
    skusTagged: 'SKUs tagged',
    sellableSub: '{{n}} sellable',
    questionsSub: '{{n}} question(s)',
    ambiguousSub: '{{n}} ambiguous',
    ofSub: 'of {{n}}',
  },
  tab: {
    ready: 'Ready to use ({{n}})',
    open: 'Needs a decision ({{n}})',
    norule: 'No rule yet ({{n}})',
    rules: 'Rules ({{n}})',
  },
  exportExcel: 'Export Excel',
  building: 'Building…',
  loading: 'Loading…',
  searchAny: 'Search any column…',
  clearTools: 'Clear sort & filters',
  rowsN: '{{n}} rows',
  rowsOf: '{{n}} of {{total}} rows',
  save: 'Save',
  saving: 'Saving…',
  cancel: 'Cancel',
  edit: 'Edit',
  action: 'Action',
  rowNo: 'No.',
  nothingMatches: 'Nothing matches.',
  editTagsTitle: 'Edit the six tags',
  noTagRights: 'You do not have rights to edit tags',
  ready: {
    head: 'Mapped codes — ready to use',
    note: "Every material the rules already answer — finished goods, trading goods, non-valuated items and services together, with <b>Material type</b> saying which is which. In the source workbook's column order. Click a header to sort, the funnel to filter by value — the export takes exactly what is left on screen. <b>Edit</b> on a row makes the six tags, the <b>New Hierarchy Line Code</b> and the <b>New Hierarchy Brand Code</b> editable. A tag belongs to the SKU; the line and the brand belong to the STYLE, so changing either moves every SKU of that style on that code — Save reports how many.",
    untaggedOnly: 'Only items with no tag yet ({{n}})',
    lineChanged: 'Line changed: {{note}}',
    movedLine: '{{style}} → line {{line}} · {{n}} material(s)',
    movedBrand: '{{style}} → brand {{brand}} · {{n}} material(s)',
    movedBoth: '{{style}} → line {{line}} + brand {{brand}} · {{n}} material(s)',
    saveFailed: 'Save failed ({{status}})',
    lineSaveFailed: 'Line save failed ({{status}})',
    brandSaveFailed: 'Brand save failed ({{status}})',
  },
  open: {
    head: 'Needs one decision — {{n}} question(s)',
    answered: 'Answered: {{text}}',
    decided: '{{style}} → {{line}} · {{n}} material(s)',
    note: 'These Brand + Line + Category + Sub-category combinations appear in the source workbook with more than one new Line, and it does not name this style. One answer per row classifies every SKU under it.',
    answer: 'Answer',
    pick: 'Pick…',
    classifyTitle: 'Classify every SKU under this row',
    noAnswerRights: 'You do not have rights to answer this',
    saveFailed: 'Could not save the decision',
    itemsHead: 'Every item waiting — {{n}}',
    itemsNote: 'The same materials as the questions above, one row each, so it is visible which SKUs an answer moves. The new code columns are empty by definition — that is what the question above decides. Candidates is what the rule offers for that code.',
  },
  norule: {
    groupedHead: 'No rule yet — grouped by Brand + Line',
    groupedNote: 'The source workbook never mentions these codes. Each row is one scope question: in or out.',
    colBrandLine: 'Brand / Line',
    colCodes: 'Codes',
    sellableHead: 'Sellable items with no rule — {{n}}',
    sellableNote: 'Finished goods (FERT) sitting on a hierarchy code the source workbook never mentions. The new code columns are empty on purpose — there is no rule to fill them, and this is the list that has to be answered before there is.',
    otherHead: 'Other material types with no rule — {{n}}',
    otherNote: 'The rest of the 6,124: everything the material master does not call a finished good — <b>HAWA</b> trading goods (bought and resold, so these may well need a rule too), <b>UNBW</b> non-valuated and <b>DIEN</b> service. Same codes, same question.',
  },
  rules: {
    head: 'Rules — brand + old line + category + sub-category → new line (and brand)',
    add: 'Add a rule',
    note: 'A material takes its new line from the rule matching its four old-code parts. Change a rule and every material on it moves — <b>Materials</b> says how many before you do. Leave <b>New brand</b> empty and the brand follows the new line, as it always has; fill it in and the rule decides the brand instead — which is how a TERG product keeps brand 41 on a line that is not TERG. Nothing here reaches SAP, NetSuite or PLM.',
    fBrandCode: 'Brand code',
    fOldLine: 'Old line',
    fCategory: 'Category',
    fSubCategory: 'Sub-category',
    fNewLine: 'New line',
    fNewBrand: 'New brand',
    fNote: 'Note',
    followLine: '— follow the line',
    brandFollowsLine: '— brand follows the line',
    count: '{{n}} of {{total}} rules',
    removeTitle: 'Remove this rule',
    added: 'Rule added — {{n}} material(s) on it',
    updated: 'Rule updated — {{n}} material(s) on it',
    removed: 'Rule removed — {{n}} material(s) went back to "No rule yet"',
    confirmRemove: '{{n}} material(s) are classified by this rule.\n\nRemoving it puts them back into "No rule yet". Continue?',
  },
  exp: {
    title: 'Product Line Reclassification — {{label}}',
    rowsOf: '{{n}} of {{total}} rows',
    search: 'Search: {{q}}',
    failed: 'Export failed ({{status}})',
    labelMapped: 'mapped codes',
    sheetMapped: 'SKU Split Hierarchy',
    labelNorule: 'items with no rule yet',
    sheetNorule: 'No rule yet',
    labelOther: 'other material types with no rule yet',
    sheetOther: 'No rule — other types',
    labelDecisions: 'one decision each',
    sheetDecisions: 'Decisions',
    labelDecideItems: 'every item waiting on a decision',
    sheetDecideItems: 'Items waiting',
  },
  // SAP's material types (MARA-MTART) in the team's words; the codes themselves stay SAP's.
  mt: {
    fert: 'Finished goods',
    hawa: 'Trading goods',
    unbw: 'Non-valuated',
    dien: 'Service',
  },
  col: {
    styleName: 'Style Name',
    styleNameHint: 'A service or non-valuated item has no style — its own material name is shown instead',
    itemNameTitle: 'This material has no style. Its own name is shown.',
    color: 'Color',
    sellingSku: 'Selling SKU',
    sapCode: 'SAP code',
    materialType: 'Material type',
    currentCode: 'Current Hierarchy code',
    newCode: 'New Hierarchy code',
    brandCode: 'Hierarchy Brand Code',
    newBrandCode: 'New Hierarchy Brand Code',
    brandName: 'Current Brand',
    newBrandName: 'New Brand',
    lineCode: 'Hierarchy Line Code',
    newLineCode: 'New Hierarchy Line Code',
    lineName: 'Current Line',
    newLineName: 'New Line',
    categoryCode: 'Hierarchy Category Code',
    category: 'Category',
    subCategoryCode: 'Hierarchy Sub Category Code',
    subCategoryName: 'Sub Category',
    tagDependent: 'Dependent part',
    tagStandalone: 'Standalone accessory',
    tagOneOff: 'One-off',
    tagCollab: 'Collaboration',
    tagB2b: 'B2B',
    tagMarketing: 'Marketing use',
    parts: 'Old code parts',
    style: 'Style',
    candidates: 'Candidates',
    materials: 'Materials',
    sellable: 'Sellable',
    example: 'Example',
    brand: 'Brand',
    brandNameRule: 'Brand name',
    oldLine: 'Old line',
    oldLineName: 'Old line name',
    categoryNameRule: 'Category name',
    subCategory: 'Sub-category',
    subCategoryNameRule: 'Sub-category name',
    newLine: 'New line',
    newLineNameRule: 'New line name',
    newBrand: 'New brand',
    newBrandHint: 'Empty means the brand follows the new line, which is how every rule behaved before',
    newBrandNameRule: 'New brand name',
    source: 'Source',
    updatedBy: 'Updated by',
    materialName: 'Material name',
  },
};

const ko = {
  title: '제품 라인 재분류',
  subtitle: '새 계층 코드와 6개 태그를 포털에서 관리합니다 — SAP, NetSuite, PLM은 읽기 전용입니다',
  whereStands: '매핑 현황',
  refresh: '새로고침',
  mappingNote: '새 코드는 SAP 코드에서 <b>브랜드</b>와 <b>라인</b> 구간을 바꾼 것입니다 — 카테고리와 서브 카테고리는 그대로 유지됩니다. 브랜드는 스타일이 옮겨 가는 라인을 따르며, 편집 행에서 스타일 하나에 대해 따로 지정할 수도 있습니다. SAP, NetSuite, PLM에는 아무것도 다시 기록되지 않으며, 새 분류는 <b>아직 다른 화면에 적용되지 않습니다</b>.',
  stat: {
    mapped: '매핑됨',
    needsDecision: '결정 필요',
    noRule: '규칙 없음',
    rules: '규칙',
    skusTagged: '태그된 SKU',
    sellableSub: '판매 가능 {{n}}',
    questionsSub: '질문 {{n}}건',
    ambiguousSub: '모호 {{n}}건',
    ofSub: '전체 {{n}}개 중',
  },
  tab: {
    ready: '사용 가능 ({{n}})',
    open: '결정 필요 ({{n}})',
    norule: '규칙 없음 ({{n}})',
    rules: '규칙 ({{n}})',
  },
  exportExcel: '엑셀 내보내기',
  building: '생성 중…',
  loading: '불러오는 중…',
  searchAny: '모든 열 검색…',
  clearTools: '정렬 · 필터 해제',
  rowsN: '{{n}}행',
  rowsOf: '{{total}}행 중 {{n}}행',
  save: '저장',
  saving: '저장 중…',
  cancel: '취소',
  edit: '편집',
  action: '작업',
  rowNo: '번호',
  nothingMatches: '해당 항목이 없습니다.',
  editTagsTitle: '6개 태그 편집',
  noTagRights: '태그를 편집할 권한이 없습니다',
  ready: {
    head: '매핑된 코드 — 사용 가능',
    note: '규칙이 이미 답을 주는 모든 자재입니다 — 완제품, 상품, 비평가 자재, 서비스를 함께 보여 주며, <b>자재 유형</b>이 어느 것인지 알려 줍니다. 원본 워크북의 열 순서를 따릅니다. 헤더를 클릭하면 정렬, 깔때기를 누르면 값으로 필터링되며, 내보내기는 화면에 남은 그대로 가져갑니다. 행의 <b>편집</b>을 누르면 6개 태그, <b>새 계층 라인 코드</b>, <b>새 계층 브랜드 코드</b>를 편집할 수 있습니다. 태그는 SKU에 속하고 라인과 브랜드는 스타일에 속하므로, 둘 중 하나를 바꾸면 그 코드에 있는 해당 스타일의 모든 SKU가 함께 이동합니다 — 저장 시 몇 개인지 알려 줍니다.',
    untaggedOnly: '태그 없는 항목만 ({{n}})',
    lineChanged: '라인 변경: {{note}}',
    movedLine: '{{style}} → 라인 {{line}} · 자재 {{n}}개',
    movedBrand: '{{style}} → 브랜드 {{brand}} · 자재 {{n}}개',
    movedBoth: '{{style}} → 라인 {{line}} + 브랜드 {{brand}} · 자재 {{n}}개',
    saveFailed: '저장 실패 ({{status}})',
    lineSaveFailed: '라인 저장 실패 ({{status}})',
    brandSaveFailed: '브랜드 저장 실패 ({{status}})',
  },
  open: {
    head: '결정 하나가 필요함 — 질문 {{n}}건',
    answered: '답변 완료: {{text}}',
    decided: '{{style}} → {{line}} · 자재 {{n}}개',
    note: '이 브랜드 + 라인 + 카테고리 + 서브 카테고리 조합은 원본 워크북에 새 라인이 둘 이상으로 나와 있고, 이 스타일을 지정하지 않습니다. 행마다 답 하나로 그 아래 모든 SKU가 분류됩니다.',
    answer: '답변',
    pick: '선택…',
    classifyTitle: '이 행 아래 모든 SKU 분류',
    noAnswerRights: '답변할 권한이 없습니다',
    saveFailed: '결정을 저장하지 못했습니다',
    itemsHead: '대기 중인 전체 항목 — {{n}}',
    itemsNote: '위 질문과 같은 자재를 한 행씩 보여 주어, 답이 어떤 SKU를 옮기는지 알 수 있습니다. 새 코드 열은 정의상 비어 있습니다 — 그것이 위의 질문이 정하는 것입니다. 후보는 해당 코드에 대해 규칙이 제시하는 값입니다.',
  },
  norule: {
    groupedHead: '규칙 없음 — 브랜드 + 라인별',
    groupedNote: '원본 워크북은 이 코드들을 전혀 언급하지 않습니다. 각 행은 범위에 관한 질문 하나입니다: 포함인가, 제외인가.',
    colBrandLine: '브랜드 / 라인',
    colCodes: '코드 수',
    sellableHead: '규칙이 없는 판매 가능 항목 — {{n}}',
    sellableNote: '원본 워크북이 언급하지 않는 계층 코드에 있는 완제품(FERT)입니다. 새 코드 열은 일부러 비워 두었습니다 — 채울 규칙이 없으며, 규칙이 생기기 전에 답해야 할 목록이 바로 이것입니다.',
    otherHead: '규칙이 없는 기타 자재 유형 — {{n}}',
    otherNote: '6,124개 중 나머지: 자재 마스터가 완제품으로 보지 않는 모든 것 — <b>HAWA</b> 상품(구매 후 재판매하므로 규칙이 필요할 수도 있음), <b>UNBW</b> 비평가 자재, <b>DIEN</b> 서비스입니다. 같은 코드, 같은 질문입니다.',
  },
  rules: {
    head: '규칙 — 브랜드 + 이전 라인 + 카테고리 + 서브 카테고리 → 새 라인 (및 브랜드)',
    add: '규칙 추가',
    note: '자재는 이전 코드의 네 부분과 일치하는 규칙에서 새 라인을 가져옵니다. 규칙을 바꾸면 그 규칙에 걸린 모든 자재가 이동합니다 — 바꾸기 전에 <b>자재</b> 열이 몇 개인지 알려 줍니다. <b>새 브랜드</b>를 비워 두면 지금까지처럼 브랜드가 새 라인을 따르고, 채우면 규칙이 브랜드를 정합니다 — TERG 제품이 TERG가 아닌 라인에서도 브랜드 41을 유지하는 방법입니다. 여기의 어떤 것도 SAP, NetSuite, PLM에 반영되지 않습니다.',
    fBrandCode: '브랜드 코드',
    fOldLine: '이전 라인',
    fCategory: '카테고리',
    fSubCategory: '서브 카테고리',
    fNewLine: '새 라인',
    fNewBrand: '새 브랜드',
    fNote: '메모',
    followLine: '— 라인을 따름',
    brandFollowsLine: '— 브랜드가 라인을 따름',
    count: '규칙 {{total}}개 중 {{n}}개',
    removeTitle: '이 규칙 삭제',
    added: '규칙 추가됨 — 자재 {{n}}개 적용',
    updated: '규칙 수정됨 — 자재 {{n}}개 적용',
    removed: '규칙 삭제됨 — 자재 {{n}}개가 "규칙 없음"으로 돌아갔습니다',
    confirmRemove: '이 규칙으로 분류된 자재가 {{n}}개 있습니다.\n\n삭제하면 "규칙 없음"으로 돌아갑니다. 계속할까요?',
  },
  exp: {
    title: '프로덕트 라인 재분류 — {{label}}',
    rowsOf: '{{total}}행 중 {{n}}행',
    search: '검색: {{q}}',
    failed: '내보내기 실패 ({{status}})',
    labelMapped: '매핑된 코드',
    sheetMapped: 'SKU 계층 분할',
    labelNorule: '규칙이 없는 항목',
    sheetNorule: '규칙 없음',
    labelOther: '규칙이 없는 기타 자재 유형',
    sheetOther: '규칙 없음 — 기타 유형',
    labelDecisions: '질문마다 결정 하나',
    sheetDecisions: '결정',
    labelDecideItems: '결정을 기다리는 모든 항목',
    sheetDecideItems: '대기 항목',
  },
  mt: {
    fert: '완제품',
    hawa: '상품',
    unbw: '비평가 자재',
    dien: '서비스',
  },
  col: {
    styleName: '스타일명',
    styleNameHint: '서비스나 비평가 항목에는 스타일이 없어 자재명이 대신 표시됩니다',
    itemNameTitle: '이 자재에는 스타일이 없어 자재 자체의 이름을 표시합니다.',
    color: '색상',
    sellingSku: '판매 SKU',
    sapCode: 'SAP 코드',
    materialType: '자재 유형',
    currentCode: '현재 계층 코드',
    newCode: '새 계층 코드',
    brandCode: '계층 브랜드 코드',
    newBrandCode: '새 계층 브랜드 코드',
    brandName: '현재 브랜드',
    newBrandName: '새 브랜드',
    lineCode: '계층 라인 코드',
    newLineCode: '새 계층 라인 코드',
    lineName: '현재 라인',
    newLineName: '새 라인',
    categoryCode: '계층 카테고리 코드',
    category: '카테고리',
    subCategoryCode: '계층 서브 카테고리 코드',
    subCategoryName: '서브 카테고리',
    tagDependent: '종속 부속',
    tagStandalone: '단독 액세서리',
    tagOneOff: '일회성',
    tagCollab: '콜라보레이션',
    tagB2b: 'B2B',
    tagMarketing: '마케팅 활용',
    parts: '이전 코드 구성',
    style: '스타일',
    candidates: '후보',
    materials: '자재',
    sellable: '판매 가능',
    example: '예시',
    brand: '브랜드',
    brandNameRule: '브랜드명',
    oldLine: '이전 라인',
    oldLineName: '이전 라인명',
    categoryNameRule: '카테고리명',
    subCategory: '서브 카테고리',
    subCategoryNameRule: '서브 카테고리명',
    newLine: '새 라인',
    newLineNameRule: '새 라인명',
    newBrand: '새 브랜드',
    newBrandHint: '비워 두면 브랜드가 새 라인을 따릅니다 — 이전의 모든 규칙이 그렇게 동작했습니다',
    newBrandNameRule: '새 브랜드명',
    source: '출처',
    updatedBy: '수정자',
    materialName: '자재명',
  },
};

const vi = {
  title: 'Phân loại lại dòng sản phẩm',
  subtitle: 'Mã phân cấp mới và sáu tag, lưu trên portal — SAP, NetSuite và PLM chỉ đọc',
  whereStands: 'Tình trạng ánh xạ',
  refresh: 'Làm mới',
  mappingNote: 'Mã mới là mã SAP với phần <b>Thương hiệu</b> và <b>Dòng</b> được thay thế — Danh mục và Danh mục con giữ nguyên. Thương hiệu đi theo dòng mà style được chuyển sang, và cũng có thể đặt riêng cho một style ở hàng Sửa. Không có gì được ghi ngược về SAP, NetSuite hay PLM, và phân loại mới <b>chưa được áp dụng trên màn hình nào khác</b>.',
  stat: {
    mapped: 'Đã ánh xạ',
    needsDecision: 'Cần quyết định',
    noRule: 'Chưa có quy tắc',
    rules: 'Quy tắc',
    skusTagged: 'SKU đã gắn tag',
    sellableSub: '{{n}} bán được',
    questionsSub: '{{n}} câu hỏi',
    ambiguousSub: '{{n}} chưa rõ ràng',
    ofSub: 'trên {{n}}',
  },
  tab: {
    ready: 'Sẵn sàng dùng ({{n}})',
    open: 'Cần quyết định ({{n}})',
    norule: 'Chưa có quy tắc ({{n}})',
    rules: 'Quy tắc ({{n}})',
  },
  exportExcel: 'Xuất Excel',
  building: 'Đang tạo…',
  loading: 'Đang tải…',
  searchAny: 'Tìm trong mọi cột…',
  clearTools: 'Xóa sắp xếp & bộ lọc',
  rowsN: '{{n}} dòng',
  rowsOf: '{{n}} / {{total}} dòng',
  save: 'Lưu',
  saving: 'Đang lưu…',
  cancel: 'Hủy',
  edit: 'Sửa',
  action: 'Thao tác',
  rowNo: 'STT',
  nothingMatches: 'Không có kết quả phù hợp.',
  editTagsTitle: 'Sửa sáu tag',
  noTagRights: 'Bạn không có quyền sửa tag',
  ready: {
    head: 'Mã đã ánh xạ — sẵn sàng dùng',
    note: 'Mọi vật tư đã có quy tắc trả lời — thành phẩm, hàng thương mại, hàng không định giá và dịch vụ cùng một bảng, cột <b>Loại vật tư</b> cho biết loại nào. Theo thứ tự cột của workbook nguồn. Nhấn tiêu đề để sắp xếp, nhấn phễu để lọc theo giá trị — file xuất lấy đúng những gì còn lại trên màn hình. Nút <b>Sửa</b> trên một dòng cho phép sửa sáu tag, <b>Mã dòng phân cấp mới</b> và <b>Mã thương hiệu phân cấp mới</b>. Tag thuộc về SKU; dòng và thương hiệu thuộc về STYLE, nên đổi một trong hai sẽ chuyển mọi SKU của style đó trên mã đó — khi Lưu sẽ báo bao nhiêu.',
    untaggedOnly: 'Chỉ mục chưa có tag ({{n}})',
    lineChanged: 'Đã đổi dòng: {{note}}',
    movedLine: '{{style}} → dòng {{line}} · {{n}} vật tư',
    movedBrand: '{{style}} → thương hiệu {{brand}} · {{n}} vật tư',
    movedBoth: '{{style}} → dòng {{line}} + thương hiệu {{brand}} · {{n}} vật tư',
    saveFailed: 'Lưu thất bại ({{status}})',
    lineSaveFailed: 'Lưu dòng thất bại ({{status}})',
    brandSaveFailed: 'Lưu thương hiệu thất bại ({{status}})',
  },
  open: {
    head: 'Cần một quyết định — {{n}} câu hỏi',
    answered: 'Đã trả lời: {{text}}',
    decided: '{{style}} → {{line}} · {{n}} vật tư',
    note: 'Các tổ hợp Thương hiệu + Dòng + Danh mục + Danh mục con này xuất hiện trong workbook nguồn với hơn một Dòng mới, và workbook không nêu tên style này. Một câu trả lời cho mỗi dòng sẽ phân loại mọi SKU thuộc dòng đó.',
    answer: 'Trả lời',
    pick: 'Chọn…',
    classifyTitle: 'Phân loại mọi SKU thuộc dòng này',
    noAnswerRights: 'Bạn không có quyền trả lời mục này',
    saveFailed: 'Không lưu được quyết định',
    itemsHead: 'Mọi mục đang chờ — {{n}}',
    itemsNote: 'Cùng các vật tư của những câu hỏi ở trên, mỗi vật tư một dòng, để thấy một câu trả lời sẽ chuyển những SKU nào. Các cột mã mới để trống theo định nghĩa — đó chính là điều câu hỏi ở trên quyết định. Cột Ứng viên là những gì quy tắc đề xuất cho mã đó.',
  },
  norule: {
    groupedHead: 'Chưa có quy tắc — nhóm theo Thương hiệu + Dòng',
    groupedNote: 'Workbook nguồn không hề nhắc đến các mã này. Mỗi dòng là một câu hỏi về phạm vi: có hay không.',
    colBrandLine: 'Thương hiệu / Dòng',
    colCodes: 'Số mã',
    sellableHead: 'Mặt hàng bán được chưa có quy tắc — {{n}}',
    sellableNote: 'Thành phẩm (FERT) nằm trên mã phân cấp mà workbook nguồn không nhắc đến. Các cột mã mới cố ý để trống — chưa có quy tắc để điền, và đây là danh sách cần được trả lời trước khi có quy tắc.',
    otherHead: 'Các loại vật tư khác chưa có quy tắc — {{n}}',
    otherNote: 'Phần còn lại của 6,124: mọi thứ mà dữ liệu gốc vật tư không coi là thành phẩm — <b>HAWA</b> hàng thương mại (mua về rồi bán lại, nên rất có thể cũng cần quy tắc), <b>UNBW</b> không định giá và <b>DIEN</b> dịch vụ. Cùng mã, cùng câu hỏi.',
  },
  rules: {
    head: 'Quy tắc — thương hiệu + dòng cũ + danh mục + danh mục con → dòng mới (và thương hiệu)',
    add: 'Thêm quy tắc',
    note: 'Một vật tư lấy dòng mới từ quy tắc khớp với bốn phần của mã cũ. Đổi một quy tắc thì mọi vật tư thuộc quy tắc đó sẽ chuyển theo — cột <b>Vật tư</b> cho biết bao nhiêu trước khi bạn đổi. Để trống <b>Thương hiệu mới</b> thì thương hiệu đi theo dòng mới như trước nay; điền vào thì quy tắc sẽ quyết định thương hiệu — đó là cách một sản phẩm TERG giữ thương hiệu 41 trên một dòng không phải TERG. Không có gì ở đây được đưa sang SAP, NetSuite hay PLM.',
    fBrandCode: 'Mã thương hiệu',
    fOldLine: 'Dòng cũ',
    fCategory: 'Danh mục',
    fSubCategory: 'Danh mục con',
    fNewLine: 'Dòng mới',
    fNewBrand: 'Thương hiệu mới',
    fNote: 'Ghi chú',
    followLine: '— theo dòng',
    brandFollowsLine: '— thương hiệu theo dòng',
    count: '{{n}} / {{total}} quy tắc',
    removeTitle: 'Xóa quy tắc này',
    added: 'Đã thêm quy tắc — áp dụng cho {{n}} vật tư',
    updated: 'Đã cập nhật quy tắc — áp dụng cho {{n}} vật tư',
    removed: 'Đã xóa quy tắc — {{n}} vật tư quay về "Chưa có quy tắc"',
    confirmRemove: 'Có {{n}} vật tư đang được phân loại theo quy tắc này.\n\nXóa quy tắc sẽ đưa chúng về "Chưa có quy tắc". Tiếp tục?',
  },
  exp: {
    title: 'Phân loại lại Product Line — {{label}}',
    rowsOf: '{{n}} / {{total}} dòng',
    search: 'Tìm: {{q}}',
    failed: 'Xuất thất bại ({{status}})',
    labelMapped: 'mã đã ánh xạ',
    sheetMapped: 'Phân cấp SKU',
    labelNorule: 'mục chưa có quy tắc',
    sheetNorule: 'Chưa có quy tắc',
    labelOther: 'loại vật tư khác chưa có quy tắc',
    sheetOther: 'Chưa có quy tắc — loại khác',
    labelDecisions: 'mỗi câu hỏi một quyết định',
    sheetDecisions: 'Quyết định',
    labelDecideItems: 'mọi mục đang chờ quyết định',
    sheetDecideItems: 'Mục đang chờ',
  },
  mt: {
    fert: 'Thành phẩm',
    hawa: 'Hàng thương mại',
    unbw: 'Không định giá',
    dien: 'Dịch vụ',
  },
  col: {
    styleName: 'Tên style',
    styleNameHint: 'Dịch vụ hoặc hàng không định giá không có style — hiển thị tên vật tư thay thế',
    itemNameTitle: 'Vật tư này không có style. Đang hiển thị tên của chính nó.',
    color: 'Màu',
    sellingSku: 'SKU bán hàng',
    sapCode: 'Mã SAP',
    materialType: 'Loại vật tư',
    currentCode: 'Mã phân cấp hiện tại',
    newCode: 'Mã phân cấp mới',
    brandCode: 'Mã thương hiệu phân cấp',
    newBrandCode: 'Mã thương hiệu phân cấp mới',
    brandName: 'Thương hiệu hiện tại',
    newBrandName: 'Thương hiệu mới',
    lineCode: 'Mã dòng phân cấp',
    newLineCode: 'Mã dòng phân cấp mới',
    lineName: 'Dòng hiện tại',
    newLineName: 'Dòng mới',
    categoryCode: 'Mã danh mục phân cấp',
    category: 'Danh mục',
    subCategoryCode: 'Mã danh mục con phân cấp',
    subCategoryName: 'Danh mục con',
    tagDependent: 'Bộ phận phụ thuộc',
    tagStandalone: 'Phụ kiện độc lập',
    tagOneOff: 'Một lần',
    tagCollab: 'Hợp tác',
    tagB2b: 'B2B',
    tagMarketing: 'Dùng cho marketing',
    parts: 'Các phần mã cũ',
    style: 'Style',
    candidates: 'Ứng viên',
    materials: 'Vật tư',
    sellable: 'Bán được',
    example: 'Ví dụ',
    brand: 'Thương hiệu',
    brandNameRule: 'Tên thương hiệu',
    oldLine: 'Dòng cũ',
    oldLineName: 'Tên dòng cũ',
    categoryNameRule: 'Tên danh mục',
    subCategory: 'Danh mục con',
    subCategoryNameRule: 'Tên danh mục con',
    newLine: 'Dòng mới',
    newLineNameRule: 'Tên dòng mới',
    newBrand: 'Thương hiệu mới',
    newBrandHint: 'Để trống nghĩa là thương hiệu theo dòng mới, như mọi quy tắc trước đây',
    newBrandNameRule: 'Tên thương hiệu mới',
    source: 'Nguồn',
    updatedBy: 'Người cập nhật',
    materialName: 'Tên vật tư',
  },
};

const zh = {
  title: '产品线重新分类',
  subtitle: '新层级代码和六个标签保存在门户中——SAP、NetSuite 和 PLM 仅供读取',
  whereStands: '映射现状',
  refresh: '刷新',
  mappingNote: '新代码是将 SAP 代码中的<b>品牌</b>和<b>产品线</b>两段替换后的结果——类别和子类别保持不变。品牌跟随款式被移入的产品线，也可以在编辑行中为单个款式单独设定。不会回写到 SAP、NetSuite 或 PLM，新分类<b>尚未应用于任何其他页面</b>。',
  stat: {
    mapped: '已映射',
    needsDecision: '待决定',
    noRule: '尚无规则',
    rules: '规则',
    skusTagged: '已标记 SKU',
    sellableSub: '可售 {{n}}',
    questionsSub: '{{n}} 个问题',
    ambiguousSub: '{{n}} 个有歧义',
    ofSub: '共 {{n}}',
  },
  tab: {
    ready: '可直接使用 ({{n}})',
    open: '待决定 ({{n}})',
    norule: '尚无规则 ({{n}})',
    rules: '规则 ({{n}})',
  },
  exportExcel: '导出 Excel',
  building: '生成中…',
  loading: '加载中…',
  searchAny: '搜索任意列…',
  clearTools: '清除排序和筛选',
  rowsN: '{{n}} 行',
  rowsOf: '{{n}} / {{total}} 行',
  save: '保存',
  saving: '保存中…',
  cancel: '取消',
  edit: '编辑',
  action: '操作',
  rowNo: '序号',
  nothingMatches: '没有匹配项。',
  editTagsTitle: '编辑六个标签',
  noTagRights: '您没有编辑标签的权限',
  ready: {
    head: '已映射代码 — 可直接使用',
    note: '规则已能回答的所有物料——成品、贸易商品、非估价物料和服务一并列出，<b>物料类型</b>说明各属哪类。按源工作簿的列顺序排列。点击表头排序，点击漏斗按值筛选——导出的内容与屏幕上剩下的完全一致。点击行上的<b>编辑</b>可编辑六个标签、<b>新层级产品线代码</b>和<b>新层级品牌代码</b>。标签属于 SKU；产品线和品牌属于款式，因此更改其中任一项都会移动该款式在此代码下的所有 SKU——保存时会显示数量。',
    untaggedOnly: '仅显示尚无标签的项目 ({{n}})',
    lineChanged: '产品线已更改：{{note}}',
    movedLine: '{{style}} → 产品线 {{line}} · {{n}} 个物料',
    movedBrand: '{{style}} → 品牌 {{brand}} · {{n}} 个物料',
    movedBoth: '{{style}} → 产品线 {{line}} + 品牌 {{brand}} · {{n}} 个物料',
    saveFailed: '保存失败 ({{status}})',
    lineSaveFailed: '产品线保存失败 ({{status}})',
    brandSaveFailed: '品牌保存失败 ({{status}})',
  },
  open: {
    head: '需要一个决定 — {{n}} 个问题',
    answered: '已回答：{{text}}',
    decided: '{{style}} → {{line}} · {{n}} 个物料',
    note: '这些品牌 + 产品线 + 类别 + 子类别的组合在源工作簿中对应不止一个新产品线，且工作簿未指明此款式。每行给出一个答案，即可为其下所有 SKU 分类。',
    answer: '回答',
    pick: '请选择…',
    classifyTitle: '为此行下的所有 SKU 分类',
    noAnswerRights: '您没有回答此项的权限',
    saveFailed: '无法保存该决定',
    itemsHead: '所有待处理项目 — {{n}}',
    itemsNote: '与上方问题相同的物料，每个一行，便于查看一个答案会移动哪些 SKU。新代码列按定义为空——这正是上方问题要决定的。候选项是规则为该代码提供的选项。',
  },
  norule: {
    groupedHead: '尚无规则 — 按品牌 + 产品线分组',
    groupedNote: '源工作簿从未提及这些代码。每行是一个范围问题：纳入还是排除。',
    colBrandLine: '品牌 / 产品线',
    colCodes: '代码数',
    sellableHead: '尚无规则的可售项目 — {{n}}',
    sellableNote: '位于源工作簿从未提及的层级代码上的成品 (FERT)。新代码列特意留空——没有规则可以填充，这正是在有规则之前需要回答的清单。',
    otherHead: '尚无规则的其他物料类型 — {{n}}',
    otherNote: '6,124 个中的其余部分：物料主数据不视为成品的所有物料——<b>HAWA</b> 贸易商品（采购后转售，因此很可能也需要规则）、<b>UNBW</b> 非估价物料和 <b>DIEN</b> 服务。相同的代码，相同的问题。',
  },
  rules: {
    head: '规则 — 品牌 + 旧产品线 + 类别 + 子类别 → 新产品线（及品牌）',
    add: '添加规则',
    note: '物料从与其旧代码四个部分相匹配的规则中获得新产品线。修改一条规则，其下所有物料都会随之移动——<b>物料</b>列会在您修改前显示数量。<b>新品牌</b>留空时，品牌照旧跟随新产品线；填写后则由规则决定品牌——这样 TERG 产品在非 TERG 产品线上也能保留品牌 41。此处的任何内容都不会进入 SAP、NetSuite 或 PLM。',
    fBrandCode: '品牌代码',
    fOldLine: '旧产品线',
    fCategory: '类别',
    fSubCategory: '子类别',
    fNewLine: '新产品线',
    fNewBrand: '新品牌',
    fNote: '备注',
    followLine: '— 跟随产品线',
    brandFollowsLine: '— 品牌跟随产品线',
    count: '{{n}} / {{total}} 条规则',
    removeTitle: '删除此规则',
    added: '已添加规则 — 涉及 {{n}} 个物料',
    updated: '已更新规则 — 涉及 {{n}} 个物料',
    removed: '已删除规则 — {{n}} 个物料回到“尚无规则”',
    confirmRemove: '有 {{n}} 个物料由此规则分类。\n\n删除后它们将回到“尚无规则”。是否继续？',
  },
  exp: {
    title: '产品线重新分类 — {{label}}',
    rowsOf: '{{n}} / {{total}} 行',
    search: '搜索：{{q}}',
    failed: '导出失败 ({{status}})',
    labelMapped: '已映射代码',
    sheetMapped: 'SKU 层级拆分',
    labelNorule: '尚无规则的项目',
    sheetNorule: '尚无规则',
    labelOther: '尚无规则的其他物料类型',
    sheetOther: '尚无规则 — 其他类型',
    labelDecisions: '每个问题一个决定',
    sheetDecisions: '决定',
    labelDecideItems: '所有待决定项目',
    sheetDecideItems: '待处理项目',
  },
  mt: {
    fert: '成品',
    hawa: '贸易商品',
    unbw: '非估价物料',
    dien: '服务',
  },
  col: {
    styleName: '款式名称',
    styleNameHint: '服务或非估价项目没有款式——改为显示其物料名称',
    itemNameTitle: '此物料没有款式，显示的是其自身名称。',
    color: '颜色',
    sellingSku: '销售 SKU',
    sapCode: 'SAP 代码',
    materialType: '物料类型',
    currentCode: '当前层级代码',
    newCode: '新层级代码',
    brandCode: '层级品牌代码',
    newBrandCode: '新层级品牌代码',
    brandName: '当前品牌',
    newBrandName: '新品牌',
    lineCode: '层级产品线代码',
    newLineCode: '新层级产品线代码',
    lineName: '当前产品线',
    newLineName: '新产品线',
    categoryCode: '层级类别代码',
    category: '类别',
    subCategoryCode: '层级子类别代码',
    subCategoryName: '子类别',
    tagDependent: '从属部件',
    tagStandalone: '独立配件',
    tagOneOff: '一次性',
    tagCollab: '联名合作',
    tagB2b: 'B2B',
    tagMarketing: '营销用途',
    parts: '旧代码组成',
    style: '款式',
    candidates: '候选项',
    materials: '物料',
    sellable: '可售',
    example: '示例',
    brand: '品牌',
    brandNameRule: '品牌名称',
    oldLine: '旧产品线',
    oldLineName: '旧产品线名称',
    categoryNameRule: '类别名称',
    subCategory: '子类别',
    subCategoryNameRule: '子类别名称',
    newLine: '新产品线',
    newLineNameRule: '新产品线名称',
    newBrand: '新品牌',
    newBrandHint: '留空表示品牌跟随新产品线，以前的所有规则都是如此',
    newBrandNameRule: '新品牌名称',
    source: '来源',
    updatedBy: '更新人',
    materialName: '物料名称',
  },
};

const dictionary = { ns: 'xPl', en, ko, vi, zh };

export default dictionary;
